import { CSVPreviewData } from '../types';

/**
 * Parse CSV text content into structured data for preview
 */
export function parseCSVContent(
  csvText: string,
  maxPreviewRows: number = 10000 // Increase limit to 10k rows
): CSVPreviewData {
  try {
    // Split into lines and handle different line endings
    const lines = csvText.split(/\r?\n/);

    if (lines.length === 0) {
      throw new Error('Empty CSV file');
    }

    // Parse headers from first line
    const headerLine = lines[0];
    if (!headerLine || headerLine.trim() === '') {
      throw new Error('CSV file has no headers');
    }

    const headers = parseCSVLine(headerLine);

    if (headers.length === 0) {
      throw new Error('CSV file has no columns');
    }

    // Parse data rows
    const dataLines = lines.slice(1).filter(line => line.trim() !== '');
    const totalRows = dataLines.length;

    // Limit preview rows for performance
    const previewLines = dataLines.slice(0, maxPreviewRows);
    const rows = previewLines.map(line => parseCSVLine(line, headers.length));

    return {
      headers,
      rows,
      totalRows,
      previewRows: rows.length,
    };
  } catch (error) {
    throw new Error(
      `Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Parse a single CSV line, handling quoted values and commas
 */
function parseCSVLine(line: string, expectedColumns?: number): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i += 2;
        continue;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }

    i++;
  }

  // Add the last field
  result.push(current.trim());

  // Pad with empty strings if we have fewer columns than expected
  if (expectedColumns && result.length < expectedColumns) {
    while (result.length < expectedColumns) {
      result.push('');
    }
  }

  return result;
}

/**
 * Validate CSV content before parsing
 */
export function validateCSVContent(csvText: string): {
  valid: boolean;
  error?: string;
} {
  try {
    if (!csvText || csvText.trim() === '') {
      return { valid: false, error: 'File is empty' };
    }

    const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');

    if (lines.length === 0) {
      return { valid: false, error: 'No data found in file' };
    }

    if (lines.length === 1) {
      return {
        valid: false,
        error: 'File contains only headers, no data rows',
      };
    }

    // Try to parse the first few lines to validate structure
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine);

    if (headers.length === 0) {
      return { valid: false, error: 'No columns found in CSV' };
    }

    // Check if first data row has reasonable structure
    if (lines.length > 1) {
      const firstDataLine = lines[1];
      const firstRowData = parseCSVLine(firstDataLine, headers.length);

      // Allow some flexibility in column count
      if (firstRowData.length === 0) {
        return { valid: false, error: 'Data rows appear to be empty' };
      }
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: `Invalid CSV format: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Get CSV file statistics
 */
export function getCSVStats(csvText: string): {
  totalLines: number;
  dataRows: number;
  columns: number;
  estimatedSize: number;
} {
  const lines = csvText.split(/\r?\n/);
  const nonEmptyLines = lines.filter(line => line.trim() !== '');
  const headers =
    nonEmptyLines.length > 0 ? parseCSVLine(nonEmptyLines[0]) : [];

  return {
    totalLines: lines.length,
    dataRows: Math.max(0, nonEmptyLines.length - 1), // Subtract header row
    columns: headers.length,
    estimatedSize: csvText.length,
  };
}
